import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import TabButton from '../../components/button/TabButton';
import SubTab from '../../components/button/SubTab';
import PageLayout from '../../components/layout/PageLayout';
import UserManagement from './components/UserManagement';
import BannerManagement from './components/BannerManagement';
import ReviewManagement from './components/ReviewManagement';
import AlbumManagement from './components/AlbumManagement';
import SponsoredCampaignManagement from './components/SponsoredCampaignManagement';
import EventAdManagement from './components/EventAdManagement';
import StartupPopupManagement from './components/StartupPopupManagement';
import PopupDisplayManagement from './components/PopupDisplayManagement';
import SplashAdManagement from './components/SplashAdManagement';
import NotificationPermissionPromptManagement from './components/NotificationPermissionPromptManagement';
import SupportPromotionManagement from './components/SupportPromotionManagement';
import ManualEventPushManagement from './components/ManualEventPushManagement';
import ContentSafetyManagement from './components/ContentSafetyManagement';
import FeedManagement from './components/FeedManagement';
import { adminApi } from '../../api/admin';
import { AdminDraftProvider } from './useAdminDraftGuard';

// 1depth 탭 정의
const MAIN_TABS = [
  { id: 'user', label: '회원 관리' },
  { id: 'review', label: '리뷰 관리' },
  { id: 'album', label: '앨범 관리' },
  { id: 'ads', label: '광고 관리' },
  { id: 'app', label: '앱 운영' },
  { id: 'moderation', label: '신고 관리' },
];

// 2depth 탭 정의
const SUB_TABS = {
  user: [
    { id: 'active', label: '활동 회원 관리' },
    { id: 'suspended', label: '활동정지 회원 관리' },
    { id: 'withdrawn', label: '강제탈퇴 회원 관리' },
  ],
  banner: [],
  review: [
    { id: 'active-review', label: '활성 리뷰 관리' },
    { id: 'p-mark', label: 'P 마크 리뷰 관리' },
  ],
  album: [
    { id: 'home-screen', label: '홈 화면 관리' },
    { id: 'direct-catalog', label: '음악 직접 등록' },
    { id: 'direct-apple', label: '공식 정보 연결' },
    { id: 'hidden-album', label: '숨긴 앨범 관리' },
  ],
  ads: [
    { id: 'campaigns', label: '콘텐츠 광고' },
    { id: 'home-banner', label: '홈 상단 롤링 이미지' },
    { id: 'events', label: '이벤트 관리' },
    { id: 'startup-popup', label: '첫 접속 팝업' },
    { id: 'mini-popup', label: '미니 팝업' },
    { id: 'splash', label: '스플래시 광고' },
  ],
  app: [
    { id: 'feed', label: '포스트팝 피드' },
    { id: 'notification-permission', label: '알림 권한 안내' },
    { id: 'event-push', label: '이벤트·광고 푸시' },
    { id: 'support-promotion', label: '후원 안내 배치' },
  ],
  moderation: [],
};

// subTab id -> counts key 매핑
const COUNT_KEY_MAP = {
  active: 'active',
  suspended: 'suspended',
  withdrawn: 'withdrawn',
  'active-review': 'activeReview',
  'p-mark': 'pMark',
  'hidden-album': 'hiddenAlbum',
};

const Admin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [counts, setCounts] = useState({});

  // URL에서 탭 상태 파생
  const legacyBanner = searchParams.get('mainTab') === 'banner';
  const mainTab = legacyBanner ? 'ads' : searchParams.get('mainTab') || 'user';
  const subTab = legacyBanner
    ? 'home-banner'
    : searchParams.get('subTab') || SUB_TABS[mainTab]?.[0]?.id || '';

  const fetchCounts = useCallback(async () => {
    try {
      const response = await adminApi.getCounts();
      setCounts(response.data);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (legacyBanner) {
      const next = new URLSearchParams(searchParams);
      next.set('mainTab', 'ads');
      next.set('subTab', 'home-banner');
      setSearchParams(next, { replace: true });
    }
  }, [legacyBanner, searchParams, setSearchParams]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    if (mainTab === 'review' && subTab === 'home-review') {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('mainTab', 'album');
      newSearchParams.set('subTab', 'home-screen');
      newSearchParams.delete('search');
      newSearchParams.delete('page');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [mainTab, searchParams, setSearchParams, subTab]);

  // 카운트가 포함된 서브탭 목록 생성
  const subTabsWithCounts = (SUB_TABS[mainTab] || []).map((tab) => {
    const countKey = COUNT_KEY_MAP[tab.id];
    const count = countKey != null ? counts[countKey] : undefined;
    return count != null ? { ...tab, label: `${tab.label} (${count.toLocaleString()})` } : tab;
  });

  // URL 업데이트 헬퍼
  const updateURL = (updates) => {
    const newSearchParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (key === 'page' && value === 1)
      ) {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value);
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  const onMainTabChange = (newTab) => {
    const firstSubTab = SUB_TABS[newTab]?.[0]?.id || '';
    updateURL({ mainTab: newTab, subTab: firstSubTab || null, search: null, page: null });
  };

  const onSubTabChange = (newSubTab) => {
    updateURL({ subTab: newSubTab, search: null, page: null });
  };

  // 메인 컨텐츠 렌더링
  const renderContent = () => {
    switch (mainTab) {
      case 'user':
        return (
          <UserManagement
            subTab={subTab}
            searchParams={searchParams}
            updateURL={updateURL}
            onCountsChange={fetchCounts}
          />
        );
      case 'banner':
        return <BannerManagement />;
      case 'review':
        return (
          <ReviewManagement
            subTab={subTab}
            searchParams={searchParams}
            updateURL={updateURL}
            onCountsChange={fetchCounts}
          />
        );
      case 'album':
        return (
          <AlbumManagement
            subTab={subTab || 'home-screen'}
            searchParams={searchParams}
            updateURL={updateURL}
            onCountsChange={fetchCounts}
          />
        );
      case 'ads':
        return subTab === 'home-banner' ? (
          <BannerManagement />
        ) : subTab === 'splash' ? (
          <SplashAdManagement />
        ) : subTab === 'startup-popup' ? (
          <StartupPopupManagement />
        ) : subTab === 'mini-popup' ? (
          <PopupDisplayManagement mini />
        ) : subTab === 'events' ? (
          <EventAdManagement />
        ) : (
          <SponsoredCampaignManagement />
        );
      case 'app':
        return subTab === 'feed' ? (
          <FeedManagement />
        ) : subTab === 'support-promotion' ? (
          <SupportPromotionManagement />
        ) : subTab === 'event-push' ? (
          <ManualEventPushManagement />
        ) : (
          <NotificationPermissionPromptManagement />
        );
      case 'moderation':
        return <ContentSafetyManagement />;
      default:
        return null;
    }
  };

  return (
    <PageLayout>
      <div className="admin-page">
        {/* 1depth 탭 */}
        <TabButton
          activeTab={mainTab}
          onTabChange={onMainTabChange}
          tabs={MAIN_TABS}
          centered={false}
          marginTop={{ xs: 3.75, lg: 5 }}
        >
          {/* 2depth 탭 */}
          {subTabsWithCounts.length > 0 && (
            <SubTab
              activeTab={subTab}
              onTabChange={onSubTabChange}
              tabs={subTabsWithCounts}
              sx={{ mb: { xs: 3.75, lg: 5 } }}
            />
          )}

          {/* 컨텐츠 */}
          {renderContent()}
        </TabButton>
      </div>
    </PageLayout>
  );
};

export default function GuardedAdmin() {
  return (
    <AdminDraftProvider>
      <Admin />
    </AdminDraftProvider>
  );
}
